import { useState } from "react";
import { Shield, Loader2, User, Star, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";
import { trackEvent } from "@/lib/analytics";

const FreshBilling = () => {
  const { user, session } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleCheckout = async (type: "starter" | "pro" | "refill") => {
    if (!user || !session) { setShowAuthModal(true); return; }
    setLoading(type);
    trackEvent("checkout_started", { plan: type });
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { type },
      });
      if (error) throw new Error(error.message);
      if (data?.url) window.open(data.url, "_blank");
      else throw new Error("No checkout URL returned");
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-3xl w-full space-y-8">
        <div className="text-center space-y-2">
          <div className="w-16 h-16 rounded-full bg-cyan/20 flex items-center justify-center mx-auto">
            <Shield className="w-8 h-8 text-cyan" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Buy Credit Bundles</h1>
          <p className="text-muted-foreground">Decode messages, unlock response strategy, and generate content. Credits never expire.</p>
          {!user && (
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
              <User className="w-3.5 h-3.5" /> Sign in required to purchase
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
          {/* Starter Pack */}
          <div className="p-5 rounded-xl border border-border bg-card flex flex-col">
            <h3 className="font-semibold text-foreground">Starter Pack</h3>
            <p className="text-2xl font-bold text-foreground mt-1">$9.99</p>
            <p className="text-xs text-muted-foreground mb-3">25 credits · ~$0.40/credit</p>
            <div className="space-y-1.5 text-left mb-4 flex-1">
              <Feature text="25 credits added instantly" />
              <Feature text="Full forensic analysis" />
              <Feature text="Response strategy" />
              <Feature text="Content generation" />
              <Feature text="Credits never expire" />
            </div>
            <button onClick={() => handleCheckout("starter")} disabled={!!loading} className={cn("w-full py-2.5 rounded-lg font-semibold text-sm bg-secondary border border-border text-foreground hover:border-cyan/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed")}>
              {loading === "starter" ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Buy Starter Pack"}
            </button>
          </div>

          {/* Pro Pack */}
          <div className="p-5 rounded-xl border-2 border-cyan/50 bg-gradient-to-b from-cyan/10 to-transparent flex flex-col relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-cyan text-xs font-bold text-primary-foreground flex items-center gap-1"><Star className="w-3 h-3" />Best Value</div>
            <h3 className="font-semibold text-cyan">Pro Pack</h3>
            <p className="text-2xl font-bold text-foreground mt-1">$24.99</p>
            <p className="text-xs text-muted-foreground mb-3">80 credits · ~$0.31/credit</p>
            <div className="space-y-1.5 text-left mb-4 flex-1">
              <Feature text="80 credits added instantly" />
              <Feature text="Everything in Starter" />
              <Feature text="Best value per credit" />
              <Feature text="Ideal for creators" />
            </div>
            <button onClick={() => handleCheckout("pro")} disabled={!!loading} className={cn("btn-glow w-full py-2.5 rounded-lg font-semibold text-sm bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100")}>
              {loading === "pro" ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Buy Pro Pack"}
            </button>
          </div>

          {/* 10-Credit Pack */}
          <div className="p-5 rounded-xl border border-border bg-card flex flex-col">
            <h3 className="font-semibold text-foreground">10-Credit Pack</h3>
            <p className="text-2xl font-bold text-foreground mt-1">$4.99</p>
            <p className="text-xs text-muted-foreground mb-3">10 credits · ~$0.50/credit</p>
            <p className="text-xs text-muted-foreground/70 mb-4 flex-1">
              Buy it on its own or add to your balance.
            </p>
            <button onClick={() => handleCheckout("refill")} disabled={!!loading} className={cn("w-full py-2.5 rounded-lg font-semibold text-sm bg-secondary border border-border text-foreground hover:border-cyan/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed")}>
              {loading === "refill" ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Buy 10-Credit Pack — $4.99"}
            </button>
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground/60 text-center">
          7-day refund policy · Credits never expire · Added instantly upon purchase
        </p>
      </div>

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </div>
  );
};

function Feature({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Check className="w-3 h-3 text-cyan shrink-0" />
      <span>{text}</span>
    </div>
  );
}

export default FreshBilling;
